import router from "@/router";
import { getConnectAccountToken } from "@/services/stripe.service";
import {
  ChangeSessionPayload,
  CreateAccount,
  CreateUser,
  FirebaseUser,
  LoginUser,
} from "@/types/auth";
import { RootState } from "@/types/store";
import { get, set, toggle } from "@/utils/vuex";
import { get as lodashGet } from "lodash";
import { AxiosResponse } from "axios";
import {
  createUserWithEmailAndPassword,
  getAuth,
  sendEmailVerification,
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signOut,
  updateProfile,
  ActionCodeSettings,
} from "firebase/auth";
import firebase from "firebase/compat";
import moment from "moment-timezone";
import { ActionContext } from "vuex";
import axios from "@/plugins/axios";
import i18n from "@/plugins/i18n";
import { Navigator as CustomNavigator } from "@/types/global";
import config from "@/configs";
import User = firebase.User;

const navigator: CustomNavigator = window.navigator;

interface State {
  user: FirebaseUser | null;
  authUser: User | null;
  loading: boolean;
  loadingSession: boolean;
  loadingEmailLink: boolean;
  loadingResetPassword: boolean;
}

const SIGN_IN_EMAIL_KEY = "emailForSignIn";

export default {
  namespaced: true,
  state: (): State => ({
    user: null,
    authUser: null,
    loading: false,
    loadingSession: false,
    loadingEmailLink: false,
    loadingResetPassword: false,
  }),
  mutations: {
    setUser: set("user"),
    setAuthUser: set("authUser"),
    setLoading: set("loading"),
    toggleLoading: toggle("loading"),
    toggleLoadingSession: toggle("loadingSession"),
    toggleLoadingEmailLink: toggle("loadingEmailLink"),
    toggleLoadingResetPassword: toggle("loadingResetPassword"),
  },
  getters: {
    getUser: get("user"),
    getUserAccounts: get("user.accounts"),
    getUserCustomers: get("user.customers"),
    getLoading: get("loading"),
    getLoadingSession: get("loadingSession"),
    getLoadingEmailLink: get("loadingEmailLink"),
    getLoadingResetPassword: get("loadingResetPassword"),
    getAuthDisplayName: get("authUser.displayName"),
    getAuthEmail: get("authUser.email"),
    getAuthEmailVerified: get("authUser.emailVerified"),
    getAuthInitials(state: State): string {
      const authName = state.authUser?.displayName || "";

      const getInitials = (name: string) => {
        const parts = name.split(" ") ?? [];
        let initials;

        if (parts.length > 1) {
          const first = (parts.shift() ?? "").charAt(0);
          const last = (parts.pop() ?? "").charAt(0);
          initials = `${first}${last}`.trim();
        } else {
          initials = name.substring(0, 2);
        }

        return initials.toUpperCase();
      };

      return getInitials(authName);
    },
  },
  actions: {
    async processToken(
      context: ActionContext<State, RootState>,
      { token }: { token: string }
    ): Promise<boolean> {
      return axios
        .post(`/api/token/${token}/process`)
        .then(() => {
          return true;
        })
        .catch((e: AxiosResponse) => {
          context.commit(
            "snackbar/toastError",
            { message: e.data },
            { root: true }
          );

          return false;
        });
    },
    async fetchTokenInfo(
      context: ActionContext<State, RootState>,
      { token }: { token: string }
    ): Promise<AxiosResponse> {
      return await axios.get(`/public/token/${token}/info`);
    },
    isLoggedIn(): boolean {
      const auth = getAuth();

      return !!auth.currentUser;
    },
    async logout(
      context: ActionContext<State, RootState>,
      // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
      { redirect = true }
    ): Promise<void> {
      const auth = getAuth();
      await signOut(auth);

      if (redirect) await router.push("/login");
    },
    async login(
      context: ActionContext<State, RootState>,
      payload: LoginUser
    ): Promise<void> {
      const { email, password, token } = payload;
      const auth = getAuth();

      context.commit("toggleLoading");

      const result = await signInWithEmailAndPassword(auth, email, password)
        .then(() => {
          context.commit(
            "snackbar/toastSuccess",
            { message: i18n.t("success.login") },
            { root: true }
          );

          return true;
        })
        .catch((error) => {
          context.commit(
            "snackbar/toastError",
            { message: error.message },
            { root: true }
          );

          return false;
        })
        .finally(() => {
          context.commit("toggleLoading");
        });

      if (result) {
        if (token) await context.dispatch("processToken", { token });

        context.dispatch("afterLogin");
      }
    },
    async updateUser(context: ActionContext<State, RootState>): Promise<void> {
      const timezone = moment.tz.guess();
      const language = navigator.languages
        ? navigator.languages[0]
        : navigator.language || navigator.userLanguage;
      const payload = {
        timezone,
        language,
      };
      const me = await axios.patch("/api/auth/me", payload);
      context.commit("setUser", me);
    },
    async afterLogin(context: ActionContext<State, RootState>): Promise<void> {
      /**
       * If the user is on the invite page, they should be silently logged out
       * and none of the "afterLogin" proceedure should execute
       */
      if (window.location.pathname === "/invite") {
        const logoutPayload = { redirect: false };
        await context.dispatch("logout", logoutPayload);
        return;
      }

      const auth = getAuth();
      const user = auth.currentUser;

      context.commit("setAuthUser", user);

      context.commit("toggleLoading");

      await context.dispatch("fetchAndSetUser");

      context.commit("toggleLoading");

      if (router.currentRoute.name !== "landing") {
        router.push({ name: "landing" });
      }

      /**
       * Where does email verification happen
       */
    },
    async fetchAndSetUser(
      context: ActionContext<State, RootState>
    ): Promise<void> {
      const auth = getAuth();
      if (!auth.currentUser) {
        return;
      }

      const doFetchMe = async () => {
        // Performs a refresh
        await auth.currentUser?.getIdTokenResult(true);

        const response = await axios
          .get("/api/auth/me")
          .catch((error: AxiosResponse) => {
            context.commit("snackbar/toastError", error?.data || {}, {
              root: true,
            });
          });

        if (response) {
          const { data: me } = response;
          context.commit("setUser", me);
        }
      };

      if (auth.currentUser?.emailVerified) {
        await doFetchMe();
      } else {
        const checkForVerifiedInterval = setInterval(() => {
          auth.currentUser?.reload().then(async () => {
            if (auth.currentUser?.emailVerified) {
              context.commit("setLoading", true);
              await doFetchMe();
              context.commit("setLoading", false);
              clearInterval(checkForVerifiedInterval);
            }
          });
        }, 2000);
      }
    },
    async createAuthUser(
      context: ActionContext<State, RootState>,
      payload: CreateUser
    ): Promise<void> {
      const { email, password, confirmPassword, firstName, lastName, token } =
        payload;
      const displayName = `${firstName} ${lastName}`.trim();
      const auth = getAuth();

      if (!password || password.length < 6 || password !== confirmPassword) {
        context.commit(
          "snackbar/toastError",
          { message: i18n.t("errors.passwordsMustMatch") },
          { root: true }
        );

        return;
      }

      const onError = (error: Error) => {
        context.commit(
          "snackbar/toastError",
          { message: error.message },
          { root: true }
        );
        context.commit("setLoading", false);
      };

      context.commit("setLoading", true);

      try {
        // 1. Create the Firebase Auth user
        await createUserWithEmailAndPassword(auth, email, password);

        if (!auth.currentUser) {
          return;
        }

        // 2. If user was created with a token, process the token before we fetch
        if (token) {
          await context.dispatch("processToken", { token });

          // Force the token refresh so that the auth token has the emailVerified param set to true
          // from the processToken call
          await auth.currentUser?.reload();
        }

        // 3. Update the Firebase Auth user displayName value to their provided name
        await updateProfile(auth.currentUser, { displayName });

        // 4. Update the database user with browser preferences (timezone & language)
        await context.dispatch("updateUser");

        // 5. Send email verification only if it was not from an invite
        if (!token) context.dispatch("verifyEmailLink");

        // 6. Consider the user logged in at this point
        await context.dispatch("afterLogin");

        context.commit("setLoading", false);
        context.commit(
          "snackbar/toastSuccess",
          { message: i18n.t("accounts.accountCreated") },
          { root: true }
        );
      } catch (e: any) {
        onError(e);
      }
    },
    async createAccount(
      context: ActionContext<State, RootState>,
      data: CreateAccount
    ): Promise<void> {
      context.commit("toggleLoading");

      const payload: any = {
        business_profile: {
          name: "",
        },
        company: null,
        individual: null,
        business_type: data.business_type,
        tos_shown_and_accepted: data.tos_shown_and_accepted,
        country: data.country,
      };

      let name = "";
      if (data.business_type === "company") {
        delete data.individual;
        name = data.company?.name || "";
      } else if (data.business_type === "individual") {
        delete data.company;
        name = `${data.individual?.first_name} ${data.individual?.last_name}`;
        payload.individual = {
          first_name: data.individual?.first_name,
          last_name: data.individual?.last_name,
        };
      }
      payload.company = { name };
      payload.business_profile.name = name;

      if (config?.stripe.connectAccountType === "custom") {
        const response = await getConnectAccountToken(data).catch(
          (error: Error) => {
            context.commit("snackbar/toastError", error, { root: true });
          }
        );

        const { token } = response;
        const { id: tokenId } = token;

        payload.account_token = tokenId;
      }

      const account = await axios
        .post("/api/account", payload)
        .then((response: AxiosResponse) => response.data)
        .catch((error: AxiosResponse) => {
          context.commit("snackbar/toastError", error.data, { root: true });
        })
        .finally(() => {
          context.commit("toggleLoading");
        });

      if (account) {
        const { id: accountId } = account;

        await context.dispatch("changeSession", {
          accountId,
        });
      }
    },
    async changeSession(
      context: ActionContext<State, RootState>,
      payload: ChangeSessionPayload
    ): Promise<void> {
      const { accountId } = payload || {};
      const auth = getAuth();
      context.commit("toggleLoadingSession");

      // Update our session to prepare for SSO
      await axios
        .post("/api/auth/session", {
          setSSO: true,
          account: accountId,
        })
        .then(async () => {
          // Perform a refresh to update iat, which will have the new session sso set
          const encodedToken = await auth.currentUser?.getIdToken(true);
          const decodedToken = await auth.currentUser?.getIdTokenResult(true);
          const role = lodashGet(decodedToken, "claims.role");
          const ssoEndpoint = config?.apps.platformSso;

          const roleRouteKey = lodashGet(
            config,
            `settings.roleRedirectApp.${role}`
          );
          const roleEndpoint = lodashGet(config, `apps.${roleRouteKey}`);

          roleEndpoint
            ? (window.location.href = `${roleEndpoint}?token=${encodedToken}&lang=${i18n.locale}`)
            : (window.location.href = `${ssoEndpoint}?token=${encodedToken}&lang=${i18n.locale}`);
        })
        .catch((error: AxiosResponse) => {
          context.commit("snackbar/toastError", error.data, { root: true });
        })
        .finally(() => {
          context.commit("toggleLoadingSession");
        });
    },
    async verifyEmailLink(
      context: ActionContext<State, RootState>
    ): Promise<boolean> {
      const auth = getAuth();
      context.commit("toggleLoadingEmailLink");

      let res = false;
      if (auth.currentUser) {
        res = await sendEmailVerification(auth.currentUser)
          .then(() => {
            context.commit("toggleLoadingEmailLink");

            return true;
          })
          .catch((error) => {
            context.commit("toggleLoadingEmailLink");
            context.commit("snackbar/toastError", error, { root: true });

            return false;
          });
      }

      return res;
    },
    async sendEmailLink(
      context: ActionContext<State, RootState>,
      email: string
    ): Promise<void> {
      const auth = getAuth();
      const destinationSignInUrl = config?.settings.destinationSignInUrl || "";

      const actionCodeSettings: ActionCodeSettings = {
        url: destinationSignInUrl,
        handleCodeInApp: true,
      };
      await sendSignInLinkToEmail(auth, email, actionCodeSettings)
        .then(() => {
          context.commit(
            "snackbar/toastSuccess",
            { message: i18n.t("success.sendEmailLink") },
            { root: true }
          );
          // Dummy key to store the email locally and don't need to ask later
          window.localStorage.setItem(SIGN_IN_EMAIL_KEY, email);
        })
        .catch(() => {
          context.commit(
            "snackbar/toastError",
            { message: i18n.t("errors.sendEmailLinkFails") },
            { root: true }
          );
        });
    },

    async loginInWithEmailLink(
      context: ActionContext<State, RootState>
    ): Promise<boolean> {
      let signInResult = false;

      if (isSignInWithEmailLink(getAuth(), window.location.href)) {
        signInResult = await signInWithEmailLink(
          getAuth(),
          window.localStorage.getItem(SIGN_IN_EMAIL_KEY) || "",
          window.location.href
        )
          .then(() => {
            window.localStorage.removeItem(SIGN_IN_EMAIL_KEY);
            context.dispatch("afterLogin");
            return true;
          })
          .catch(() => {
            context.commit(
              "snackbar/toastError",
              { message: i18n.t("errors.signInEmailLinkFails") },
              { root: true }
            );
            return false;
          });
      }
      return signInResult;
    },
    async resetPassword(
      context: ActionContext<State, RootState>,
      { email }: { email: string }
    ): Promise<boolean> {
      const auth = getAuth();
      context.commit("toggleLoadingResetPassword");

      let res = false;
      res = await sendPasswordResetEmail(auth, email)
        .then(() => {
          context.commit("toggleLoadingResetPassword");

          return true;
        })
        .catch((error) => {
          context.commit("toggleLoadingResetPassword");
          context.commit("snackbar/toastError", error, { root: true });

          return false;
        });

      return res;
    },
  },
};
